import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  CheckboxList,
  DialogOverlayActions,
  Ellipsis,
  ListItem,
  Skeleton,
  TransitioningDialogOverlay,
  usePageWidth,
  type useOverlay,
} from '@fs/zion-ui'
import { Route, useHistory } from '@fs/zion-router'
import { ContentAdd } from '@fs/zion-icon'
import playlistsConfig from '../../../../config/feature/playlist'
import PillButton from '../../../../lib/buttons/PillButton'
import UpdatePlaylistForm from '../UpdatePlaylistForm'
import { usePlaylists } from '../../../../hooks/playlist/usePlaylists'
import { usePlaylistCreation } from '../../../../hooks/playlist/usePlaylistCreation'
import { usePlaylistsByItems } from '../../../../hooks/playlist/usePlaylistsByItems'
import { usePlaylistUpdate } from '../../../../hooks/playlist/usePlaylistUpdate'
import { useFlagsContext, usePlaylistContainerContext } from '../../../../providers'
import { FLAGS } from '../../../../config'
import notifySentry from '../../../../lib/helpers/sentryNotifier'

const Loading = ({ count = 3 }): JSX.Element => (
  <>
    {Array.from({ length: count }).map((_, index) => (
      // eslint-disable-next-line react/no-array-index-key
      <Skeleton.Checkbox key={index} />
    ))}
  </>
)

const UpdatePlaylist = ({
  sessionId,
  onPlaylistUpdated,
}: {
  sessionId: string
  onPlaylistUpdated?: (playlistIds: string[]) => void
}): JSX.Element => {
  const [t] = useTranslation()
  const { [FLAGS.NEW_PLAYLIST_STATE]: newPlaylistStateFlag } = useFlagsContext()

  // New stack (React Query)
  const { playlists: nPlaylists, isFetching: isPlaylistsLoading } = usePlaylists()
  const { playlistsByItems, isLoading, isFetching } = usePlaylistsByItems(newPlaylistStateFlag.isOn ? [sessionId] : [])
  const { updatePlaylist, isPending: isUpdating } = usePlaylistUpdate()

  // Old stack (context)
  const {
    playlists: cPlaylistsRaw,
    findPlaylistsWithItem,
    addToPlaylist,
    removeFromPlaylist,
  } = usePlaylistContainerContext()
  const cPlaylists = cPlaylistsRaw ?? new Map()
  const oldSelectedIdsRef = useRef<Set<string>>(new Set())
  const [oldLoading, setOldLoading] = useState(!newPlaylistStateFlag.isOn)
  const [oldPending, setOldPending] = useState(false)
  useEffect(() => {
    if (newPlaylistStateFlag.isOn) return
    findPlaylistsWithItem({ itemId: sessionId }).then((results: { id: string; parent?: unknown }[]) => {
      oldSelectedIdsRef.current = new Set(results.filter((p) => !p.parent).map((p) => p.id))
      setOldLoading(false)
    })
  }, [newPlaylistStateFlag.isOn, findPlaylistsWithItem, sessionId])

  const playlists = newPlaylistStateFlag.isOn ? nPlaylists : [...cPlaylists.values()].filter((p) => !p.parent)
  const [pendingId, setPendingId] = useState('')

  const onChange = useCallback(
    async (newSelectedIds: string[], lastTouched: { name: string; checked: boolean }): Promise<void> => {
      if (!lastTouched) return
      const { name: playlistId, checked } = lastTouched
      setPendingId(playlistId)
      if (newPlaylistStateFlag.isOn) {
        if (checked) {
          await updatePlaylist({ id: playlistId, addItems: [sessionId] })
        } else {
          await updatePlaylist({ id: playlistId, removeItems: [sessionId] })
        }
      } else {
        const alreadyChecked = oldSelectedIdsRef.current.has(playlistId)
        if (checked === alreadyChecked) return
        setOldPending(true)
        const previousIds = oldSelectedIdsRef.current
        try {
          if (checked) {
            oldSelectedIdsRef.current = new Set(Array.from(oldSelectedIdsRef.current).concat(playlistId))
            await addToPlaylist({ playlistId, items: [sessionId] })
          } else {
            oldSelectedIdsRef.current = new Set(Array.from(oldSelectedIdsRef.current).filter((id) => id !== playlistId))
            await removeFromPlaylist({ playlistId, itemId: sessionId })
          }
        } catch (err) {
          oldSelectedIdsRef.current = previousIds
          notifySentry(err)
        } finally {
          setOldPending(false)
        }
      }
      onPlaylistUpdated?.(newSelectedIds)
    },
    [newPlaylistStateFlag.isOn, updatePlaylist, addToPlaylist, removeFromPlaylist, sessionId, onPlaylistUpdated]
  )

  if ((newPlaylistStateFlag.isOn && (isLoading || isFetching)) || oldLoading) {
    return <Loading count={playlists.length} />
  }

  const selectedIds = newPlaylistStateFlag.isOn
    ? (playlistsByItems[sessionId]?.results.map(({ id }) => id) ?? [])
    : Array.from(oldSelectedIdsRef.current)

  const checkboxKey = newPlaylistStateFlag.isOn ? selectedIds.join(',') : playlists.map((p) => p.id).join(',')

  return (
    <CheckboxList onSelectionChange={onChange} initialSelection={selectedIds} key={checkboxKey}>
      {playlists
        .filter((p) => !p.parent)
        .map((playlist) => {
          const full = !playlist.default && playlist.size >= playlistsConfig.sessionLimit

          let secondaryText = t('playlists.playlist.size', '{ playlistSize } videos', {
            playlistSize: playlist.size || 0,
          })
          if (full) {
            secondaryText = t('playlists.playlists.size-status', '{ playlistSize } videos (FULL)', {
              playlistSize: playlist.size,
            })
          }

          const isPending = newPlaylistStateFlag.isOn
            ? (isUpdating || isPlaylistsLoading) && pendingId === playlist.id
            : oldPending && pendingId === playlist.id

          return (
            <ListItem
              data-testid={`${playlist.default ? 'watch-later' : playlist.name}-playlist`}
              dense
              disabled={full || isPending}
              primaryText={
                <Ellipsis>
                  <strong>{playlist.name}</strong>
                </Ellipsis>
              }
              secondaryText={secondaryText}
              key={playlist.id}
              name={playlist.id}
              linkName={`Add to ${playlist.default ? 'Default' : 'Other'} Playlist`}
            />
          )
        })}
    </CheckboxList>
  )
}

const CreatePlaylist = ({ sessionId }: { sessionId: string }): JSX.Element => {
  const history = useHistory()
  const { createPlaylist } = usePlaylistCreation()

  const onSubmit = async ({ name, description }: { name: string; description: string }): Promise<void> => {
    await createPlaylist({ name, description, initialItem: sessionId || undefined })
    history.goBack()
  }

  return <UpdatePlaylistForm onSubmit={onSubmit} />
}

function Wrapper({ children, height = 286 }: { children: React.ReactNode; height?: number }): JSX.Element {
  const atWidth = usePageWidth()
  return (
    <div
      data-testid="playlist-overlay-scroll-container"
      style={atWidth({ default: {}, md: { maxHeight: height, overflowX: 'hidden', overflowY: 'auto' } })}
    >
      {children}
    </div>
  )
}

export const PlaylistManagementOverlay = ({
  sessionId,
  overlay,
  onPlaylistUpdated,
}: {
  sessionId: string
  overlay: ReturnType<typeof useOverlay>
  onPlaylistUpdated?: (playlistIds: string[]) => void
}): JSX.Element => {
  const [t] = useTranslation()
  const { [FLAGS.NEW_PLAYLIST_STATE]: newPlaylistStateFlag } = useFlagsContext()
  const { playlists: nPlaylists, isLoading: nIsLoading } = usePlaylists()
  const { playlists: cPlaylistsRaw } = usePlaylistContainerContext()
  const cPlaylists = cPlaylistsRaw ?? new Map()

  const playlists = newPlaylistStateFlag.isOn ? nPlaylists : [...cPlaylists.values()].filter((p) => !p.parent)
  const isLoading = newPlaylistStateFlag.isOn && nIsLoading
  const canCreatePlaylists = playlists.filter((p) => !p.parent).length < playlistsConfig.playlistLimit

  return (
    <TransitioningDialogOverlay
      {...overlay}
      size="sm"
      title={t('playlists.overlay.heading', 'Add to...')}
      initialLocation="/update"
    >
      <Route
        path="/update"
        footer={
          <DialogOverlayActions>
            <PillButton
              Icon={ContentAdd}
              color="blue"
              disabled={!canCreatePlaylists}
              emphasis="low"
              linkName="Create Playlist"
              data-testid="create-playlist"
              to="/create"
            >
              {t('playlists.collection.create.action', 'Create Playlist')}
            </PillButton>
          </DialogOverlayActions>
        }
      >
        <Wrapper height={228}>
          {isLoading ? <Loading /> : <UpdatePlaylist sessionId={sessionId} onPlaylistUpdated={onPlaylistUpdated} />}
        </Wrapper>
      </Route>
      <Route path="/create" title={t('playlists.create.title', 'Create New Playlist')}>
        <Wrapper>
          <CreatePlaylist sessionId={sessionId} />
        </Wrapper>
      </Route>
    </TransitioningDialogOverlay>
  )
}
